import httpRequest from "@/utils/api";
import type {
  AuditLogListData,
  DashboardOverview,
  OwnerPasswordChangePayload,
  TenantConsoleAccount,
  TenantFaviconUploadResponse,
  TenantLogoUploadResponse,
  TenantOrderRecordListData,
  TenantPricingMergedListData,
  TenantPricingUpsertPayload,
  TenantSupportLinkResponse,
  TenantSettings,
  TenantSettingsPayload,
  TeamMember,
  TeamMemberListData,
  TeamMemberPasswordResetPayload,
  TeamMemberPayload,
  TenantUser,
  TenantUserListData,
} from "@/types/tenantConsole";
import { useAuthStore } from "@/stores/auth";
import {
  demoUsers,
  demoTeachers,
  demoTeamMembers,
  demoAuditLogs,
  demoDashboardStats,
  demoOrders,
  demoUserGrowth30d,
  demoRevenue6m,
} from "@/demo/demoScenario";
import { paginateDemoData, searchDemoUsers } from "@/demo/helpers";

/**
 * 检查是否为演示模式
 */
function isDemoMode(): boolean {
  const authStore = useAuthStore();
  return authStore.isDemo;
}

enum API {
  orders = "/tenant-console/orders",
  pricingConfigs = "/tenant-console/pricing-configs",
  pricingConfigByPricing = "/tenant-console/pricing-configs/by-pricing/:pricingId",
  pricingConfig = "/tenant-console/pricing-configs/:configId",
}

export const getSettingsAPI = () => {
  return httpRequest.get<TenantSettings>({
    url: "/tenant-console/settings",
  });
};

export const updateSettingsAPI = (payload: TenantSettingsPayload) => {
  return httpRequest.put<TenantSettings>({
    url: "/tenant-console/settings",
    data: payload,
    encrypt: true,
  });
};

export const getSupportLinkPreviewAPI = (payload: Pick<TenantSettingsPayload, 'supportProvider' | 'supportDisplayName' | 'supportWechatToken'>) => {
  return httpRequest.post<TenantSupportLinkResponse>({
    url: "/tenant-console/settings/support-link-preview",
    data: payload,
    encrypt: true,
  });
};

export const uploadLogoAPI = (file: File, variant: 'light' | 'dark') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('variant', variant);

  return httpRequest.post<TenantLogoUploadResponse>({
    url: "/tenant-console/settings/upload-logo",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadFaviconAPI = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return httpRequest.post<TenantFaviconUploadResponse>({
    url: "/tenant-console/settings/upload-favicon",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getUsersAPI = (params: { page: number; limit: number; query?: string }) => {
  if (isDemoMode()) {
    const filtered = searchDemoUsers(demoUsers, params.query || "");
    const paginated = paginateDemoData(filtered, params.page, params.limit);
    return Promise.resolve({
      status: 200,
      data: {
        users: paginated,
        total: filtered.length,
        page: params.page,
        limit: params.limit,
      },
    });
  }
  return httpRequest.get<TenantUserListData>({
    url: "/tenant-console/users",
    params,
  });
};

export const getUserDetailAPI = (userId: number) => {
  return httpRequest.get<TenantUser>({
    url: `/tenant-console/users/${userId}`,
  });
};

export const getTeachersAPI = (params: { page: number; limit: number; query?: string }) => {
  if (isDemoMode()) {
    const filtered = searchDemoUsers(demoTeachers, params.query || "");
    const paginated = paginateDemoData(filtered, params.page, params.limit);
    return Promise.resolve({
      status: 200,
      data: {
        users: paginated,
        total: filtered.length,
        page: params.page,
        limit: params.limit,
      },
    });
  }
  return httpRequest.get<TenantUserListData>({
    url: "/tenant-console/teachers",
    params,
  });
};

export const assignTeacherAPI = (userId: number) => {
  return httpRequest.post<TenantUser>({
    url: `/tenant-console/teachers/${userId}/assign`,
  });
};

export const revokeTeacherAPI = (userId: number) => {
  return httpRequest.post<TenantUser>({
    url: `/tenant-console/teachers/${userId}/revoke`,
  });
};

export const getAuditLogsAPI = (params: { page: number; limit: number }) => {
  if (isDemoMode()) {
    const paginated = paginateDemoData(demoAuditLogs, params.page, params.limit);
    return Promise.resolve({
      status: 200,
      data: {
        logs: paginated,
        total: demoAuditLogs.length,
        page: params.page,
        limit: params.limit,
      },
    });
  }
  return httpRequest.get<AuditLogListData>({
    url: "/tenant-console/audit-logs",
    params,
  });
};

export const getTeamMembersAPI = (params: { page: number; limit: number }) => {
  if (isDemoMode()) {
    const paginated = paginateDemoData(demoTeamMembers, params.page, params.limit);
    return Promise.resolve({
      status: 200,
      data: {
        accounts: paginated,
        total: demoTeamMembers.length,
        page: params.page,
        limit: params.limit,
      },
    });
  }
  return httpRequest.get<TeamMemberListData>({
    url: "/tenant-console/team-members",
    params,
  });
};

export const createTeamMemberAPI = (payload: TeamMemberPayload) => {
  return httpRequest.post<TeamMember>({
    url: "/tenant-console/team-members",
    data: payload,
  });
};

export const updateTeamMemberStatusAPI = (accountId: number, status: "active" | "suspended") => {
  return httpRequest.patch<TeamMember>({
    url: `/tenant-console/team-members/${accountId}/status`,
    data: { status },
  });
};

export const resetTeamMemberPasswordAPI = (
  accountId: number,
  payload: TeamMemberPasswordResetPayload,
) => {
  return httpRequest.patch<TeamMember>({
    url: `/tenant-console/team-members/${accountId}/password`,
    data: payload,
  });
};

/**
 * 修改当前所有者密码
 */
export const changeOwnerPasswordAPI = (payload: OwnerPasswordChangePayload) => {
  return httpRequest.patch<TenantConsoleAccount>({
    url: '/tenant-console/account/password',
    data: {
      old_password: payload.oldPassword,
      new_password: payload.newPassword,
    },
  });
};


export const getDashboardOverviewAPI = () => {
  if (isDemoMode()) {
    return Promise.resolve({
      status: 200,
      data: {
        stats: demoDashboardStats,
        trends: {
          userGrowth30d: demoUserGrowth30d,
          revenue6m: demoRevenue6m,
        },
      },
    });
  }
  return httpRequest.get<DashboardOverview>({
    url: "/tenant-console/dashboard/overview",
  });
};

export const getTenantOrdersAPI = (params: { page: number; limit: number }) => {
  if (isDemoMode()) {
    const paginated = paginateDemoData(demoOrders, params.page, params.limit);
    return Promise.resolve({
      status: 200,
      data: {
        records: paginated,
        total: demoOrders.length,
        page: params.page,
        limit: params.limit,
      },
    });
  }
  return httpRequest.get<TenantOrderRecordListData>({
    url: API.orders,
    params,
  });
};

/**
 * 获取租户价格配置列表（合并视图）
 */
export const getTenantPricingConfigsAPI = () => {
  return httpRequest.get<TenantPricingMergedListData>({
    url: API.pricingConfigs,
  });
};

/**
 * Upsert 租户价格配置（按 pricing_id）
 */
export const upsertTenantPricingConfigAPI = (
  pricingId: number,
  payload: TenantPricingUpsertPayload,
) => {
  return httpRequest.put<null>({
    url: API.pricingConfigByPricing.replace(":pricingId", pricingId.toString()),
    data: payload,
  });
};

/**
 * 删除租户价格配置（恢复继承）
 */
export const deleteTenantPricingConfigAPI = (configId: number) => {
  return httpRequest.delete<null>({
    url: API.pricingConfig.replace(":configId", configId.toString()),
  });
};

/**
 * 获取租户佣金记录列表
 */
export const getTenantCommissionRecordsAPI = (params: {
  page?: number;
  limit?: number;
  status?: string | null;
  startDate?: string;
  endDate?: string;
}) => {
  return httpRequest.get<any>({
    url: "/tenant-console/commission-records",
    params,
  });
};

/**
 * 获取租户佣金汇总
 */
export const getTenantCommissionSummaryAPI = () => {
  return httpRequest.get<any>({
    url: "/tenant-console/commission-summary",
  });
};
